import { normalizeSupportConsent } from "./sessionPrivacy";
import { buildStepAlignedFollowUp } from "./stepRouter";
import { buildFollowUpPlan } from "./buildFollowUpPlan";
import { buildTranscriptSourceVersion, inferTranscriptTopic } from "./transcriptVersion";

function normalizeText(value = "") {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function isOperationalSupportSummary(sessionSummary) {
  if (!sessionSummary || typeof sessionSummary !== "object") return false;
  const source = normalizeText(sessionSummary?.source).toLowerCase();
  const responseRoute = normalizeText(
    sessionSummary?.responseRoute || sessionSummary?.coachingState?.responseRoute
  ).toLowerCase();
  return Boolean(
    sessionSummary?.operationalIntent?.operational
    || sessionSummary?.coachingState?.operationalIntent?.operational
    || source === "operational-support-rule"
    || responseRoute === "support"
  );
}

export const FOLLOW_UP_TIMEZONE = "Africa/Johannesburg";
const FOLLOW_UP_OFFSET = "+02:00";
export const FOLLOW_UP_PAST_ERROR = "Scheduled follow-up time must be in the future.";
const FOLLOW_UP_PROMPT_WINDOW_MS = 24 * 60 * 60 * 1000;
const USER_FACING_RECOMMENDATION_FALLBACK =
  "Choose one clear focus to test before your next session, then notice what changed.";
const INTERNAL_FAIL_CLOSED_TEXT_PATTERN =
  /\b(?:unsupported themes?|current[-\s]session transcript|topic[-\s]specific recap|generated summary contained themes?|without carrying forward)\b/i;
const SAFETY_RECOMMENDATION_TEXT =
  "Confirm immediate safety and ensure human support or coach review follows up.";
const HIGH_CLINICAL_DISSOCIATION_RECOMMENDATION_TEXT =
  "Check whether the member has appropriate professional support for the diagnosis and memory gaps, then explore one safe relational support step.";
const STALE_RECOMMENDATION_THEME_GUARDS = [
  { label: "marketing", pattern: /\bmarketing\b/i },
  { label: "workload", pattern: /\b(workload|weekly boundary|work structure|too many moving pieces)\b/i },
  { label: "family", pattern: /\b(family|parents?|mother|father)\b/i },
  { label: "partnership", pattern: /\b(partnership|partner|reciprocity)\b/i },
  { label: "legacy", pattern: /\blegacy\b/i },
  { label: "self-trust", pattern: /\bself[-\s]?trust\b/i },
  { label: "work rhythm", pattern: /\bwork rhythm\b/i },
  { label: "boundaries", pattern: /\bboundar(?:y|ies)\b/i },
];

function looksLikeGreeting(text = "") {
  const normalized = normalizeText(text).toLowerCase();
  if (!normalized) return false;
  return (
    /^(hi|hello|hey)\b/.test(normalized) &&
    (
      normalized.includes("i'm nia") ||
      normalized.includes("i am nia") ||
      normalized.includes("your neurogres intelligent assistant")
    )
  );
}

function normalizeOptionalText(value = "", maxLength = 500) {
  const text = normalizeText(value).slice(0, maxLength);
  return text || "";
}

function isInternalFailClosedText(value = "") {
  return INTERNAL_FAIL_CLOSED_TEXT_PATTERN.test(normalizeText(value));
}

function normalizeParticipantFacingText(value = "", maxLength = 500) {
  const text = normalizeOptionalText(value, maxLength);
  return isInternalFailClosedText(text) ? "" : text;
}

function cleanSupportGrammar(value = "") {
  return normalizeText(value)
    .replace(/\bYou is\b/g, "You are")
    .replace(/\byou is\b/g, "you are")
    .replace(/\bYou was\b/g, "You were")
    .replace(/\byou was\b/g, "you were")
    .replace(/\bYou feels\b/g, "You feel")
    .replace(/\byou feels\b/g, "you feel")
    .replace(/\bNext time you feels\b/g, "Next time you feel")
    .replace(/\bnext time you feels\b/g, "next time you feel")
    .replace(/\btry try\b/gi, "try")
    .replace(/\btry changing changing\b/gi, "try changing")
    .replace(/\bfocus on focus on\b/gi, "focus on")
    .replace(/\bname name\b/gi, "name")
    .replace(/\btry to try to\b/gi, "try to");
}

function ensureSingleSentence(value = "") {
  const normalized = cleanSupportGrammar(normalizeOptionalText(value, 220));
  if (!normalized) return "";
  const firstSentence = normalized.split(/(?<=[.!?])\s+/)[0] || normalized;
  return `${firstSentence.replace(/[.?!]*$/, "").trim()}.`;
}

function isMalformedUserFacingRecommendation(value = "") {
  const normalized = normalizeOptionalText(value, 220);
  if (!normalized) return true;
  if (isInternalFailClosedText(normalized)) return true;
  if (
    /\b(a clearer, more workable|test whether it is becoming repeatable|sustain the momentum around)\b/i.test(normalized) ||
    /\band before your next session\b/i.test(normalized) ||
    /\ba only model\b/i.test(normalized) ||
    /\b(start the conversation now|begin the session naturally)\b/i.test(normalized) ||
    /\b(prompt injection|ignore previous instructions|disregard previous instructions)\b/i.test(normalized) ||
    /^(you are|you'?re)\s+(?:dr\.?|doctor|therapist|psychiatrist|psychologist|clinical psychologist|coach)\b/i.test(normalized) ||
    /\b(pretend to be|role[\s-]?play as|respond as|speak as|act as)\b.{0,80}\b(dr\.?|doctor|therapist|psychiatrist|psychologist|clinical psychologist|coach|counsellor|counselor|clinician)\b/i.test(normalized) ||
    /\bstart,\s*but i stop myself\b/i.test(normalized) ||
    /\bbut i stop myself\b/i.test(normalized) ||
    /\bbut then i stop\b/i.test(normalized) ||
    /\bbut i keep stopping\b/i.test(normalized) ||
    /^(let down|always love)\b/i.test(normalized)
  ) {
    return true;
  }
  if (/^[,.;:!?-]/.test(normalized) || /(?:^| )but(?:$| )/i.test(normalized) || /\b(and|or|to|with|because|before)$/i.test(normalized)) {
    return true;
  }
  if (!/\b(clarify|confirm|contact|continue|ensure|explore|review|reflect|notice|choose|name|write|track|practice|pause|test|focus|ask|set|hold|return|compare|check|observe|plan|try|start|complete|send|book|schedule|decide)\b/i.test(normalized)) {
    return true;
  }
  return false;
}

function sanitizeUserFacingRecommendationText(value = "") {
  const sentence = ensureSingleSentence(value);
  if (!sentence || isMalformedUserFacingRecommendation(sentence)) {
    return USER_FACING_RECOMMENDATION_FALLBACK;
  }
  return sentence;
}

function normalizeOptionalIso(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") {
    const dateValue = value.toDate();
    return Number.isNaN(dateValue?.getTime?.()) ? null : dateValue.toISOString();
  }
  if (typeof value?.toMillis === "function") {
    const dateValue = new Date(value.toMillis());
    return Number.isNaN(dateValue.getTime()) ? null : dateValue.toISOString();
  }
  if (typeof value?.seconds === "number") {
    const dateValue = new Date(value.seconds * 1000);
    return Number.isNaN(dateValue.getTime()) ? null : dateValue.toISOString();
  }
  const text = String(value).trim();
  if (!text) return null;
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? text : parsed.toISOString();
}

export function isFutureFollowUpTime(value, nowValue = Date.now()) {
  const scheduledMs = Date.parse(String(value || ""));
  const nowMs = Number(nowValue || Date.now());
  return !Number.isNaN(scheduledMs) && scheduledMs > nowMs;
}

function normalizeStatus(value) {
  const next = String(value || "").trim().toLowerCase();
  return ["suggested", "scheduled", "completed", "dismissed"].includes(next) ? next : "suggested";
}

function isFinalStatus(status) {
  return status === "completed" || status === "dismissed";
}

export function isFollowUpResumed(followUp) {
  const normalized = followUp?.nextStepText ? followUp : buildSessionFollowUp(followUp, null);
  if (!normalized || normalized.status !== "scheduled") return false;
  return Boolean(normalized.resumedAt || normalized.lastOpenedAt);
}

export function getFollowUpDisplayStatus(followUp) {
  const normalized = followUp?.nextStepText ? followUp : buildSessionFollowUp(followUp, null);
  if (!normalized?.nextStepText) return "none";
  if (normalized.active === false && normalized.status !== "scheduled") return "none";
  if (normalized.status === "scheduled" && isFollowUpResumed(normalized)) return "resumed";
  return normalized.status || "suggested";
}

function normalizeSuggestedBy(value) {
  return String(value || "").trim().toLowerCase() === "coach" ? "coach" : "nia";
}

function normalizeActorType(value) {
  return String(value || "").trim().toLowerCase() === "coach" ? "coach" : "nia";
}

function normalizeRecommendationSource(value) {
  return String(value || "").trim().toLowerCase() === "coach" ? "coach" : "nia";
}

function recommendationContextText(sessionSummary = {}) {
  return normalizeText([
    sessionSummary?.summaryText,
    sessionSummary?.suggestedNextStep,
    sessionSummary?.nextFollowUpFocus,
    Array.isArray(sessionSummary?.detectedThemes) ? sessionSummary.detectedThemes.join(" ") : "",
    Array.isArray(sessionSummary?.detectedBlocks) ? sessionSummary.detectedBlocks.join(" ") : "",
    sessionSummary?.coachingState?.currentStory,
    sessionSummary?.coachingState?.presentingIssue,
    sessionSummary?.coachingState?.immediateTarget,
    sessionSummary?.coachingState?.futureState,
  ].filter(Boolean).join(" "));
}

function hasUnsupportedRecommendationTheme(text = "", sessionSummary = {}) {
  const recommendationText = normalizeText(text);
  if (!recommendationText) return false;
  const currentContext = recommendationContextText(sessionSummary);
  return STALE_RECOMMENDATION_THEME_GUARDS.some((entry) => (
    entry.pattern.test(recommendationText) && !entry.pattern.test(currentContext)
  ));
}

function hasSafetyRecommendationLanguage(text = "") {
  return /\b(confirm immediate safety|personal safety|safety concern|self[-\s]?harm|suicid|unsafe|immediate danger|crisis support|local emergency services|human support or coach review)\b/i.test(normalizeText(text));
}

function isSafetySummary(sessionSummary = {}) {
  if (sessionSummary?.flagSuggested !== true) return false;
  const themes = Array.isArray(sessionSummary?.detectedThemes)
    ? sessionSummary.detectedThemes.join(" ")
    : "";
  const text = `${themes} ${sessionSummary?.summaryText || ""} ${sessionSummary?.suggestedNextStep || ""} ${sessionSummary?.nextFollowUpFocus || ""}`;
  const explicitlyNonPersonalOrResolved = /\b(?:no|not an?|without a) (?:current |active |personal )?safety concern\b|\b(?:another|other) person(?:'s)? (?:current )?(?:safety|risk|concern)\b|\bmember (?:is|was) safe\b/i.test(text);
  const explicitSafety = /\b(safety concern|safety-adjacent|self[-\s]?harm|immediate safety|close to going under|fear of breaking down)\b/i.test(text);
  const flaggedSupport = Boolean(sessionSummary?.flagSuggested) &&
    /\b(human support|coach review)\b/i.test(text);
  return !explicitlyNonPersonalOrResolved &&
    (explicitSafety || flaggedSupport) &&
    !isPracticalClarificationSummary(sessionSummary);
}

function isHighClinicalDissociationSummary(sessionSummary = {}) {
  const themes = Array.isArray(sessionSummary?.detectedThemes)
    ? sessionSummary.detectedThemes.join(" ")
    : "";
  const text = normalizeText([
    themes,
    sessionSummary?.summaryText,
    sessionSummary?.suggestedNextStep,
    sessionSummary?.nextFollowUpFocus,
  ].filter(Boolean).join(" "));
  return /\b(DID|dissociative identity disorder|dissociation|DID-related confusion|memory gaps?|switches|parts? fighting|hidden internal chaos|diagnosis)\b/i.test(text) &&
    /\b(professional support|qualified mental-health professional|partner|relationship|fear of abandonment|safe relational support step)\b/i.test(text);
}

function isPracticalClarificationSummary(sessionSummary = {}) {
  const themes = Array.isArray(sessionSummary?.detectedThemes)
    ? sessionSummary.detectedThemes.join(" ")
    : "";
  return /\b(practical clarification|task support)\b/i.test(`${themes} ${sessionSummary?.summaryText || ""}`);
}

function isLowContextPlaceholderSummary(sessionSummary = {}) {
  const text = normalizeText([
    sessionSummary?.summaryText,
    sessionSummary?.suggestedNextStep,
    sessionSummary?.nextFollowUpFocus,
    Array.isArray(sessionSummary?.detectedThemes) ? sessionSummary.detectedThemes.join(" ") : "",
  ].filter(Boolean).join(" "));
  return /\bsummary will appear once this session has more context\b/i.test(text)
    || /\blow-context-placeholder\b/i.test(sessionSummary?.source || "");
}

function isSchedulingOnlySummary(sessionSummary = {}) {
  return String(sessionSummary?.source || "").toLowerCase() === "scheduling-only-rule";
}

function hasOperationalScheduleReference(text = "") {
  return /(?:\b(?:scheduled\s+for|return\s+plan|follow[-\s]?up\s+(?:on|at|for)|reminder\s+(?:on|at|for))\b|\b(?:today|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b.{0,55}\b\d{1,2}(?::|\.)?\d{0,2}\s*(?:am|pm|a\.m\.|p\.m\.)?\b)/i.test(
    normalizeText(text)
  );
}

function sanitizeFollowUpPlanForSummary(plan = {}, sessionSummary = {}) {
  const fallbackFocusCandidate = normalizeParticipantFacingText(
    sessionSummary?.nextFollowUpFocus ||
    sessionSummary?.suggestedNextStep ||
    USER_FACING_RECOMMENDATION_FALLBACK,
    220
  );
  const fallbackFocus = isMalformedUserFacingRecommendation(fallbackFocusCandidate)
    ? USER_FACING_RECOMMENDATION_FALLBACK
    : fallbackFocusCandidate;
  const sanitizeList = (items = []) => (
    Array.isArray(items)
      ? items.filter((item) => (
          !isInternalFailClosedText(item) &&
          !hasUnsupportedRecommendationTheme(item, sessionSummary)
        ))
      : []
  );
  const nextFollowUpFocus = normalizeParticipantFacingText(
    plan?.nextFollowUpFocus,
    220
  );
  return {
    ...plan,
    nextFollowUpFocus: (
      !nextFollowUpFocus ||
      hasUnsupportedRecommendationTheme(nextFollowUpFocus, sessionSummary)
    )
      ? fallbackFocus
      : nextFollowUpFocus,
    reviewQuestions: sanitizeList(plan?.reviewQuestions),
    progressSignals: sanitizeList(plan?.progressSignals),
    correctiveIfStuck: sanitizeList(plan?.correctiveIfStuck),
  };
}

function looksLikeCompletionLanguage(text = "") {
  const normalized = normalizeText(text).toLowerCase();
  if (!normalized) return false;
  return (
    /\b(session (is )?complete|consider this complete|we can consider this complete|this is complete)\b/.test(normalized) ||
    /\b(i('| a)?m (much )?(better|okay|ok|fine) now)\b/.test(normalized) ||
    /\b(that helped|that helps).{0,20}\b(i('| a)?m done for now|we('?| a)re done for now|i can stop here)\b/.test(normalized) ||
    /\b(i('| a)?m done for now|we('?| a)re done for now|this feels resolved|it('?| i)s resolved|i feel resolved)\b/.test(normalized)
  );
}

const FOLLOW_UP_SOURCE_FIELDS = [
  "sourceMessageCount",
  "sourceLastMessageAt",
  "sourceTranscriptFingerprint",
  "sourceTopic",
];

function parseTimestamp(value) {
  const parsed = Date.parse(String(value || ""));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function latestMessageTimestamp(messages = []) {
  return (Array.isArray(messages) ? messages : []).reduce((latest, message) => Math.max(
    latest,
    parseTimestamp(
      message?.createdAt ||
      message?.sentAt ||
      message?.timestamp ||
      message?.updatedAt
    )
  ), 0);
}

function normalizeMessageRole(message = {}) {
  const role = String(message?.role || message?.sender || "").trim().toLowerCase();
  return role === "assistant" || role === "nia" ? "assistant" : "user";
}

function normalizeMessageText(message = {}) {
  return normalizeText(message?.content || message?.text || "");
}

function isScheduledFollowUp(followUp = null) {
  return Boolean(
    followUp &&
    typeof followUp === "object" &&
    String(followUp.status || "").toLowerCase() === "scheduled" &&
    followUp.scheduledFor
  );
}

function isCoachAuthoredFollowUp(followUp = null) {
  if (!followUp || typeof followUp !== "object") return false;
  return (
    normalizeActorType(followUp.source) === "coach" ||
    normalizeActorType(followUp.suggestedBy) === "coach" ||
    normalizeActorType(followUp.createdByType) === "coach" ||
    normalizeActorType(followUp.updatedByType) === "coach" ||
    followUp.coachConfirmed === true
  );
}

function laterUserMessages(candidate = {}, messages = []) {
  const entries = Array.isArray(messages) ? messages : [];
  const sourceMessageCount = Number(candidate?.sourceMessageCount);
  if (!Number.isFinite(sourceMessageCount) || sourceMessageCount < 0) return [];
  return entries
    .slice(Math.min(sourceMessageCount, entries.length))
    .filter((message) => normalizeMessageRole(message) === "user")
    .map(normalizeMessageText)
    .filter(Boolean);
}

function looksLikeExplicitFollowUpInstruction(text = "") {
  const normalized = normalizeText(text);
  if (!normalized) return false;
  return (
    /\b(?:schedule|reschedule|cancel|dismiss|remove|change|move|book|set)\b.{0,70}\b(?:follow[\s-]?up|reminder|next session|check[\s-]?in)\b/i.test(normalized) ||
    /\b(?:follow[\s-]?up|reminder|next session|check[\s-]?in)\b.{0,70}\b(?:instead|at|on|for|cancel|dismiss|remove|change|move)\b/i.test(normalized) ||
    /\b(?:do not|don'?t|no longer|no)\b.{0,35}\b(?:follow[\s-]?up|reminder|next session|check[\s-]?in)\b/i.test(normalized)
  );
}

function hasNewerSourceBoundary(candidate = {}, newer = {}) {
  const candidateCount = Number(candidate?.sourceMessageCount);
  const newerCount = Number(newer?.sourceMessageCount);
  if (Number.isFinite(candidateCount) && Number.isFinite(newerCount) && newerCount > candidateCount) {
    return true;
  }

  const candidateLastAt = parseTimestamp(candidate?.sourceLastMessageAt || candidate?.createdAt);
  const newerLastAt = parseTimestamp(newer?.sourceLastMessageAt || newer?.createdAt);
  if (candidateLastAt && newerLastAt && newerLastAt > candidateLastAt) return true;

  const candidateFingerprint = normalizeText(candidate?.sourceTranscriptFingerprint);
  const newerFingerprint = normalizeText(newer?.sourceTranscriptFingerprint);
  return Boolean(
    candidateFingerprint &&
    newerFingerprint &&
    candidateFingerprint !== newerFingerprint &&
    (!candidateLastAt || !newerLastAt || newerLastAt >= candidateLastAt)
  );
}

function getCurrentFollowUpTopic(sessionSummary = {}, messages = []) {
  return normalizeText(inferTranscriptTopic(messages) || sessionSummary?.sourceTopic).toLowerCase();
}

function hasLaterExplicitCompletion(candidate = {}, sessionSummary = {}, messages = [], explicitCompletion = false) {
  if (explicitCompletion) return true;
  if (
    sessionSummary?.followUpCompleted === true ||
    sessionSummary?.sessionCompleted === true ||
    sessionSummary?.sessionComplete === true ||
    sessionSummary?.coachingState?.sessionComplete === true ||
    normalizeText(
      sessionSummary?.completionReason ||
      sessionSummary?.coachingState?.completionReason
    )
  ) {
    return true;
  }

  const laterMessages = laterUserMessages(candidate, messages);
  if (laterMessages.some((text) => looksLikeCompletionLanguage(text))) return true;

  return Boolean(
    hasNewerSourceBoundary(candidate, sessionSummary) &&
    looksLikeCompletionLanguage([
      sessionSummary?.summaryText,
      sessionSummary?.suggestedNextStep,
      sessionSummary?.nextFollowUpFocus,
    ].filter(Boolean).join(" "))
  );
}

export function followUpSourceVersion(candidate = {}, sessionSummary = {}) {
  return FOLLOW_UP_SOURCE_FIELDS.reduce((version, field) => {
    const candidateValue = candidate?.[field];
    const summaryValue = sessionSummary?.[field];
    if (candidateValue !== undefined && candidateValue !== null && candidateValue !== "") {
      version[field] = candidateValue;
    } else if (summaryValue !== undefined && summaryValue !== null && summaryValue !== "") {
      version[field] = summaryValue;
    }
    return version;
  }, {});
}

export function supersedeStaleSuggestedFollowUp(candidate, options = {}) {
  if (!candidate || typeof candidate !== "object") return candidate || null;
  if (isScheduledFollowUp(candidate) || isCoachAuthoredFollowUp(candidate)) return candidate;

  const status = normalizeStatus(candidate.status);
  if (
    isFinalStatus(status) ||
    candidate.dismissedAt ||
    candidate.suggested === false
  ) {
    return candidate.active === false && candidate.isCurrent === false
      ? candidate
      : { ...candidate, active: false, isCurrent: false };
  }
  if (
    candidate.supersededAt ||
    candidate.supersededReason
  ) {
    return candidate.active === false && candidate.isCurrent === false
      ? candidate
      : { ...candidate, active: false, isCurrent: false };
  }

  const sessionSummary = options.sessionSummary || {};
  const messages = Array.isArray(options.messages) ? options.messages : [];
  const scheduledFollowUp = options.scheduledFollowUp || options.followUp || null;
  const newerRecommendation = options.newerRecommendation || sessionSummary?.followUpRecommendation || null;
  const sourceTopic = normalizeText(candidate.sourceTopic).toLowerCase();
  const currentTopic = getCurrentFollowUpTopic(sessionSummary, messages);
  const inferredLegacySourceTopic = sourceTopic || inferTranscriptTopic([{
    role: "user",
    content: candidate?.nextStepText || candidate?.recommendationText || "",
  }]);
  const hasSourceBoundary = Boolean(
    normalizeText(candidate?.sourceTranscriptFingerprint) ||
    Number.isFinite(Number(candidate?.sourceMessageCount)) ||
    normalizeText(candidate?.sourceLastMessageAt)
  );
  const currentTranscriptBoundary = messages.length
    ? buildTranscriptSourceVersion(messages)
    : sessionSummary;
  const transcriptBoundaryAdvanced = Boolean(
    hasSourceBoundary &&
    currentTranscriptBoundary &&
    hasNewerSourceBoundary(candidate, currentTranscriptBoundary)
  );
  const candidateTimestamp = parseTimestamp(candidate?.updatedAt || candidate?.createdAt);
  const latestMessageAt = latestMessageTimestamp(messages);
  const legacyBoundaryAdvanced = Boolean(
    !hasSourceBoundary &&
    (
      (candidateTimestamp && latestMessageAt > candidateTimestamp) ||
      (
        inferredLegacySourceTopic &&
        currentTopic &&
        inferredLegacySourceTopic !== currentTopic
      )
    )
  );
  const laterMessages = laterUserMessages(candidate, messages);
  const latestExplicitInstructionAt =
    options.latestExplicitInstructionAt ||
    sessionSummary?.latestExplicitFollowUpInstructionAt ||
    null;
  const hasLaterInstructionTimestamp = Boolean(
    latestExplicitInstructionAt &&
    parseTimestamp(latestExplicitInstructionAt) >
      parseTimestamp(candidate.sourceLastMessageAt || candidate.createdAt)
  );

  let supersededReason = "";
  if (isScheduledFollowUp(scheduledFollowUp)) {
    supersededReason = "newer_scheduled_follow_up";
  } else if (hasLaterExplicitCompletion(
    candidate,
    sessionSummary,
    messages,
    options.explicitCompletion === true
  )) {
    supersededReason = "explicit_completion";
  } else if (
    options.laterExplicitInstruction === true ||
    hasLaterInstructionTimestamp ||
    laterMessages.some(looksLikeExplicitFollowUpInstruction)
  ) {
    supersededReason = "later_explicit_instruction";
  } else if (sourceTopic && currentTopic && sourceTopic !== currentTopic) {
    supersededReason = "material_topic_change";
  } else if (legacyBoundaryAdvanced) {
    supersededReason = "legacy_source_boundary_advanced";
  } else if (
    newerRecommendation &&
    newerRecommendation !== candidate &&
    (
      options.newerRecommendation ||
      hasNewerSourceBoundary(candidate, newerRecommendation)
    )
  ) {
    supersededReason = "newer_recommendation";
  } else if (transcriptBoundaryAdvanced) {
    supersededReason = "transcript_advanced";
  }

  if (!supersededReason) return candidate;

  return {
    ...candidate,
    active: false,
    isCurrent: false,
    supersededAt:
      normalizeOptionalIso(candidate.supersededAt) ||
      normalizeOptionalIso(options.supersededAt) ||
      normalizeOptionalIso(
        scheduledFollowUp?.updatedAt ||
        newerRecommendation?.createdAt ||
        latestExplicitInstructionAt ||
        sessionSummary?.sourceLastMessageAt ||
        sessionSummary?.generatedAt
      ) ||
      new Date().toISOString(),
    supersededReason,
  };
}

function looksLikeFollowUpRecommendation(text = "") {
  const normalized = normalizeText(text).toLowerCase();
  if (!normalized || looksLikeGreeting(normalized)) return false;
  return (
    /\b(follow[\s-]?up|check[\s-]?in|check in|next session|another session|revisit this|touch base|continue this in)\b/i.test(normalized) ||
    /\b(would you like|shall we|let'?s|we can)\b.{0,60}\b(follow[\s-]?up|check[\s-]?in|check in|session)\b/i.test(normalized)
  );
}

export function getDefaultFollowUpReminderAt(scheduledFor, nowValue = Date.now()) {
  const scheduledMs = Date.parse(String(scheduledFor || ""));
  if (Number.isNaN(scheduledMs)) return null;
  const reminderMs = scheduledMs - (60 * 60 * 1000);
  return reminderMs > Number(nowValue || Date.now()) ? new Date(reminderMs).toISOString() : null;
}

export function buildSessionFollowUp(followUp, sessionSummary) {
  const nextStepCandidate = looksLikeGreeting(followUp?.nextStepText)
    ? ""
    : normalizeParticipantFacingText(followUp?.nextStepText, 220);
  const rawNextStep = nextStepCandidate;
  const summaryNextStep = normalizeParticipantFacingText(
    sessionSummary?.suggestedNextStep || sessionSummary?.nextFollowUpFocus,
    220
  );
  const staleSafetyNextStep = Boolean(
    rawNextStep &&
    sessionSummary &&
    !isSafetySummary(sessionSummary) &&
    hasSafetyRecommendationLanguage(rawNextStep)
  );
  const nextStepText = staleSafetyNextStep
    ? summaryNextStep
    : rawNextStep;
  const safeNote = normalizeParticipantFacingText(followUp?.note, 500);

  const hasExistingFollowUp = Boolean(
    followUp &&
      (
        rawNextStep ||
        safeNote ||
        followUp?.scheduledFor ||
        followUp?.reminderAt ||
        followUp?.reminderSentAt ||
        followUp?.confirmationEmailQueuedAt ||
        followUp?.confirmationEmailSentAt ||
        followUp?.confirmationEmailLastError
      )
  );

  if (!nextStepText && !hasExistingFollowUp) return null;
  const safeNextStepText = nextStepText || "Continue with the planned follow-up at the scheduled time.";

  const createdByType = normalizeActorType(followUp?.createdByType || followUp?.suggestedBy);
  const updatedByType = normalizeActorType(followUp?.updatedByType || createdByType);
  const rawStatus = followUp?.status ? normalizeStatus(followUp.status) : (nextStepText ? "suggested" : "dismissed");
  const scheduledForCandidate = normalizeOptionalIso(followUp?.scheduledFor);
  const scheduledFor = isFinalStatus(rawStatus) || isFutureFollowUpTime(scheduledForCandidate)
    ? scheduledForCandidate
    : null;
  const status = isFinalStatus(rawStatus)
    ? rawStatus
    : (scheduledFor ? "scheduled" : (rawStatus === "scheduled" ? "suggested" : rawStatus));
  const inferredCoachConfirmation = status === "scheduled" && (
    normalizeActorType(followUp?.updatedByType || createdByType) === "coach" ||
    normalizeActorType(followUp?.createdByType || createdByType) === "coach"
  );
  const coachConfirmed = Boolean(followUp?.coachConfirmed || inferredCoachConfirmation);
  const reminderAt = status === "scheduled"
    ? (
        normalizeOptionalIso(followUp?.reminderAt) ||
        (scheduledFor ? getDefaultFollowUpReminderAt(scheduledFor) : null)
      )
    : null;
  const reminderQueuedAt = status === "scheduled"
    ? normalizeOptionalIso(followUp?.reminderQueuedAt)
    : null;
  const reminderSentAt = status === "scheduled"
    ? normalizeOptionalIso(followUp?.reminderSentAt)
    : null;
  const lastOpenedAt = status === "scheduled"
    ? normalizeOptionalIso(followUp?.lastOpenedAt)
    : null;
  const resumedAt = status === "scheduled"
    ? normalizeOptionalIso(followUp?.resumedAt)
    : null;
  const confirmationEmailSentAt = status === "scheduled"
    ? normalizeOptionalIso(followUp?.confirmationEmailSentAt)
    : null;
  const confirmationEmailQueuedAt = status === "scheduled"
    ? normalizeOptionalIso(followUp?.confirmationEmailQueuedAt)
    : null;
  const normalizedFollowUp = {
    sessionId: normalizeOptionalText(followUp?.sessionId || sessionSummary?.sessionId, 120),
    nextStepText: safeNextStepText,
    suggestedBy: normalizeSuggestedBy(followUp?.suggestedBy),
    createdByType,
    updatedByType,
    coachConfirmed,
    status,
    scheduledFor,
    reminderAt,
    reminderQueuedAt,
    reminderSentAt,
    lastOpenedAt,
    resumedAt,
    confirmationEmailQueuedAt,
    confirmationEmailSentAt,
    confirmationEmailLastError: normalizeOptionalText(followUp?.confirmationEmailLastError, 240),
    confirmationEmailLastErrorAt: normalizeOptionalIso(followUp?.confirmationEmailLastErrorAt),
    reminderLastError: normalizeOptionalText(followUp?.reminderLastError, 240),
    reminderLastErrorAt: normalizeOptionalIso(followUp?.reminderLastErrorAt),
    note: safeNote,
    completionReason: normalizeOptionalText(followUp?.completionReason, 120),
    completedAt: normalizeOptionalIso(followUp?.completedAt),
    dismissedAt: normalizeOptionalIso(followUp?.dismissedAt),
    updatedAt: normalizeOptionalIso(followUp?.updatedAt) || new Date().toISOString(),
    ...followUpSourceVersion(followUp, sessionSummary),
    active: status === "scheduled"
      ? true
      : Boolean(followUp?.active !== false && !isFinalStatus(status)),
    isCurrent: status === "scheduled"
      ? true
      : Boolean(followUp?.isCurrent !== false && followUp?.active !== false && !isFinalStatus(status)),
    supersededAt: normalizeOptionalIso(followUp?.supersededAt),
    supersededReason: normalizeOptionalText(followUp?.supersededReason, 120),
  };
  return supersedeStaleSuggestedFollowUp(normalizedFollowUp, { sessionSummary });
}

export function resolveDisplaySessionFollowUp(followUp, sessionSummary, options = {}) {
  const sourceAssessed = supersedeStaleSuggestedFollowUp(followUp, {
    ...options,
    sessionSummary,
  });
  if (sourceAssessed?.active === false && sourceAssessed?.status !== "scheduled") return null;
  const normalized = buildSessionFollowUp(sourceAssessed, sessionSummary);
  if (!normalized?.nextStepText) return null;
  const assessed = supersedeStaleSuggestedFollowUp(normalized, {
    ...options,
    sessionSummary,
  });
  if (assessed?.active === false && assessed?.status !== "scheduled") return null;
  return assessed;
}

function followUpEvidenceIdentity(followUp = {}) {
  if (!followUp || typeof followUp !== "object") return "";
  return [
    normalizeOptionalText(followUp.nextStepText || followUp.recommendationText, 220),
    normalizeStatus(followUp.status || "suggested"),
    normalizeOptionalIso(followUp.scheduledFor) || "",
    normalizeOptionalText(followUp.sourceTranscriptFingerprint, 160),
    normalizeOptionalText(followUp.sourceTopic, 80),
    normalizeOptionalIso(followUp.updatedAt || followUp.createdAt) || "",
  ].join("|");
}

function isSuggestedFollowUpEvidence(followUp = {}) {
  if (!followUp || typeof followUp !== "object") return false;
  if (!normalizeOptionalText(followUp.nextStepText || followUp.recommendationText, 220)) return false;
  if (isScheduledFollowUp(followUp) || isCoachAuthoredFollowUp(followUp)) return false;
  return !isFinalStatus(normalizeStatus(followUp.status));
}

export function retainSupersededFollowUpHistory(record = {}, previousRecord = null) {
  const previous = previousRecord && typeof previousRecord === "object"
    ? previousRecord
    : {};
  const previousFollowUp = previous.followUp;
  const nextFollowUp = record?.followUp;
  const existingHistory = [
    ...(Array.isArray(record?.followUpHistory) ? record.followUpHistory : []),
    ...(Array.isArray(previous?.followUpHistory) ? previous.followUpHistory : []),
  ];

  if (
    isSuggestedFollowUpEvidence(previousFollowUp) &&
    followUpEvidenceIdentity(previousFollowUp) !== followUpEvidenceIdentity(nextFollowUp)
  ) {
    existingHistory.unshift({
      ...previousFollowUp,
      active: false,
      isCurrent: false,
      supersededAt:
        normalizeOptionalIso(previousFollowUp.supersededAt) ||
        new Date().toISOString(),
      supersededReason:
        normalizeOptionalText(previousFollowUp.supersededReason, 120) ||
        (isScheduledFollowUp(nextFollowUp)
          ? "newer_scheduled_follow_up"
          : "newer_follow_up_state"),
    });
  }

  const seen = new Set();
  const followUpHistory = existingHistory
    .filter((followUp) => followUp && typeof followUp === "object")
    .filter((followUp) => {
      const key = followUpEvidenceIdentity(followUp);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });

  return {
    ...record,
    followUpHistory,
  };
}

export function buildSessionFollowUpRecommendation(recommendation, sessionSummary, followUp = null, options = {}) {
  if (isOperationalSupportSummary(sessionSummary)) {
    return null;
  }
  const operationalFollowUp = buildSessionFollowUp(followUp, sessionSummary);
  if (!recommendation && !operationalFollowUp && isLowContextPlaceholderSummary(sessionSummary)) {
    return null;
  }
  const explicitRecommendation = recommendation && typeof recommendation === "object" ? recommendation : null;
  const assessedExplicitRecommendation = explicitRecommendation
    ? supersedeStaleSuggestedFollowUp(explicitRecommendation, {
        ...options,
        sessionSummary,
        scheduledFollowUp: operationalFollowUp,
      })
    : null;
  const explicitRecommendationSuperseded = Boolean(
    assessedExplicitRecommendation?.active === false &&
    assessedExplicitRecommendation?.supersededAt &&
    assessedExplicitRecommendation?.supersededReason !== "newer_scheduled_follow_up"
  );
  if (explicitRecommendationSuperseded) {
    const supersededRecommendationText = normalizeParticipantFacingText(
      assessedExplicitRecommendation?.recommendationText ||
      assessedExplicitRecommendation?.nextStepText,
      220
    );
    const supersededRationale = normalizeParticipantFacingText(
      assessedExplicitRecommendation?.rationale,
      320
    );
    return {
      ...explicitRecommendation,
      ...assessedExplicitRecommendation,
      ...followUpSourceVersion(assessedExplicitRecommendation, sessionSummary),
      sessionId: normalizeOptionalText(
        assessedExplicitRecommendation?.sessionId || sessionSummary?.sessionId,
        120
      ),
      suggested: assessedExplicitRecommendation?.suggested !== false,
      source: normalizeRecommendationSource(
        assessedExplicitRecommendation?.source ||
        assessedExplicitRecommendation?.suggestedBy ||
        "nia"
      ),
      recommendationText: supersededRecommendationText,
      rationale: supersededRationale,
      createdAt:
        normalizeOptionalIso(assessedExplicitRecommendation?.createdAt) ||
        normalizeOptionalIso(sessionSummary?.generatedAt) ||
        new Date().toISOString(),
      active: false,
      isCurrent: false,
      derivedFromSummary: Boolean(assessedExplicitRecommendation?.derivedFromSummary),
      stepContext: assessedExplicitRecommendation?.stepContext || sessionSummary?.currentStep || null,
      followUpPlan: sanitizeFollowUpPlanForSummary(
        assessedExplicitRecommendation?.followUpPlan || {},
        sessionSummary
      ),
    };
  }
  const explicitRecommendationText = [
    assessedExplicitRecommendation?.recommendationText,
    assessedExplicitRecommendation?.nextStepText,
    assessedExplicitRecommendation?.rationale,
    assessedExplicitRecommendation?.followUpPlan?.nextFollowUpFocus,
  ].filter(Boolean).join(" ");
  const staleSafetyRecommendation = Boolean(
    assessedExplicitRecommendation &&
    !isSafetySummary(sessionSummary) &&
    hasSafetyRecommendationLanguage(explicitRecommendationText)
  );
  const staleSchedulingRecommendation = Boolean(
    assessedExplicitRecommendation &&
    isSchedulingOnlySummary(sessionSummary) &&
    hasOperationalScheduleReference(explicitRecommendationText)
  );
  const safeExplicitRecommendation = assessedExplicitRecommendation &&
    assessedExplicitRecommendation?.supersededReason !== "newer_scheduled_follow_up" &&
    !staleSafetyRecommendation &&
    !staleSchedulingRecommendation &&
    !isInternalFailClosedText(explicitRecommendationText) &&
    !hasUnsupportedRecommendationTheme(explicitRecommendationText, sessionSummary)
      ? assessedExplicitRecommendation
      : null;
  const summaryRecommendationText = (
    !isInternalFailClosedText(sessionSummary?.suggestedNextStep) &&
    looksLikeFollowUpRecommendation(sessionSummary?.suggestedNextStep)
  )
    ? normalizeParticipantFacingText(sessionSummary?.suggestedNextStep, 220)
    : "";
  const highClinicalDissociationSummary = isHighClinicalDissociationSummary(sessionSummary);
  const followUpPlan = sanitizeFollowUpPlanForSummary(buildFollowUpPlan({
    currentGoal:
      sessionSummary?.coachingState?.futureState ||
      sessionSummary?.coachingState?.immediateTarget ||
      sessionSummary?.summaryText ||
      "",
    actions: sessionSummary?.coachingState?.actions || [],
    recommendedGames: sessionSummary?.recommendedGames || sessionSummary?.coachingState?.recommendedGames || [],
    currentStep: sessionSummary?.currentStep || sessionSummary?.coachingState?.currentStep || null,
    detectedRisks: sessionSummary?.detectedBlocks || [],
    resistanceFactors: sessionSummary?.coachingState?.internalObstacles || [],
  }), sessionSummary);
  const stepAlignedFallback = normalizeOptionalText(buildStepAlignedFollowUp(sessionSummary?.currentStep), 220);
  const planFallback = normalizeParticipantFacingText(
    sessionSummary?.nextFollowUpFocus,
    220
  ) || normalizeParticipantFacingText(followUpPlan.nextFollowUpFocus, 220);
  const safetyRecommendationText = isSafetySummary(sessionSummary) ? SAFETY_RECOMMENDATION_TEXT : "";
  const highClinicalRecommendationText = !safetyRecommendationText && highClinicalDissociationSummary
    ? HIGH_CLINICAL_DISSOCIATION_RECOMMENDATION_TEXT
    : "";
  const safeFollowUpPlan = highClinicalRecommendationText
    ? {
        ...followUpPlan,
        nextFollowUpFocus: HIGH_CLINICAL_DISSOCIATION_RECOMMENDATION_TEXT,
        reviewQuestions: [],
        progressSignals: [],
        correctiveIfStuck: [],
      }
    : isSchedulingOnlySummary(sessionSummary)
      ? {
          ...followUpPlan,
          nextFollowUpFocus: normalizeParticipantFacingText(
            sessionSummary?.nextFollowUpFocus,
            220
          ),
          reviewQuestions: [],
          progressSignals: [],
          correctiveIfStuck: [],
        }
    : followUpPlan;
  if (!safetyRecommendationText && !safeExplicitRecommendation && !summaryRecommendationText && !stepAlignedFallback && !planFallback) return null;
  const recommendationCandidates = [
    safetyRecommendationText,
    highClinicalRecommendationText,
    safeExplicitRecommendation?.recommendationText,
    safeExplicitRecommendation?.nextStepText,
    isPracticalClarificationSummary(sessionSummary) ? sessionSummary?.suggestedNextStep : "",
    summaryRecommendationText,
    planFallback,
    stepAlignedFallback,
  ].filter((candidate) => !hasUnsupportedRecommendationTheme(candidate, sessionSummary));
  const recommendationText = sanitizeUserFacingRecommendationText(
    recommendationCandidates.find((candidate) => !isMalformedUserFacingRecommendation(candidate)) || USER_FACING_RECOMMENDATION_FALLBACK,
  );
  if (!recommendationText) return null;

  const suggested = safeExplicitRecommendation?.suggested !== false;
  const source = normalizeRecommendationSource(safeExplicitRecommendation?.source || safeExplicitRecommendation?.suggestedBy || "nia");
  const createdAt =
    normalizeOptionalIso(safeExplicitRecommendation?.createdAt) ||
    normalizeOptionalIso(sessionSummary?.generatedAt) ||
    new Date().toISOString();
  const acceptedAt = normalizeOptionalIso(safeExplicitRecommendation?.acceptedAt);
  const dismissedAt = normalizeOptionalIso(safeExplicitRecommendation?.dismissedAt);
  const convertedToFollowUpAt = normalizeOptionalIso(safeExplicitRecommendation?.convertedToFollowUpAt);
  const convertedFollowUpId = normalizeOptionalText(safeExplicitRecommendation?.convertedFollowUpId, 120);
  const rawRationale = normalizeParticipantFacingText(
    safeExplicitRecommendation?.rationale,
    320
  );
  const rationale = (
    safetyRecommendationText ||
    highClinicalRecommendationText ||
    hasUnsupportedRecommendationTheme(rawRationale, sessionSummary)
  )
    ? ""
    : rawRationale;
  const active = Boolean(
    suggested &&
    recommendationText &&
    safeExplicitRecommendation?.active !== false &&
    !dismissedAt &&
    !convertedToFollowUpAt &&
    !(operationalFollowUp?.scheduledFor)
  );

  const normalizedRecommendation = {
    sessionId: normalizeOptionalText(safeExplicitRecommendation?.sessionId || sessionSummary?.sessionId, 120),
    suggested,
    source,
    recommendationText,
    rationale,
    createdAt,
    acceptedAt,
    dismissedAt,
    convertedToFollowUpAt,
    convertedFollowUpId,
    active,
    isCurrent: active,
    derivedFromSummary: !safeExplicitRecommendation,
    stepContext: sessionSummary?.currentStep || null,
    followUpPlan: safeFollowUpPlan,
    ...followUpSourceVersion(safeExplicitRecommendation, sessionSummary),
    supersededAt: normalizeOptionalIso(safeExplicitRecommendation?.supersededAt),
    supersededReason: normalizeOptionalText(safeExplicitRecommendation?.supersededReason, 120),
  };
  return supersedeStaleSuggestedFollowUp(normalizedRecommendation, {
    ...options,
    sessionSummary,
    scheduledFollowUp: operationalFollowUp,
  });
}

export function isFollowUpConfirmed(followUp) {
  const normalized = buildSessionFollowUp(followUp, null);
  if (!normalized) return false;
  return normalized.status === "scheduled" || normalized.status === "completed" || normalized.coachConfirmed;
}

export function isReminderQueued(followUp) {
  const normalized = buildSessionFollowUp(followUp, null);
  if (!normalized) return false;
  return Boolean(normalized.status === "scheduled" && normalized.scheduledFor && normalized.reminderAt && !normalized.reminderSentAt);
}

export function canQueueEmailReminder(followUp) {
  const normalized = buildSessionFollowUp(followUp, null);
  if (!normalized) return false;
  return Boolean(normalized.status === "scheduled" && normalized.scheduledFor && normalized.reminderAt);
}

export function getFollowUpStateLabel(followUp) {
  const normalized = buildSessionFollowUp(followUp, null);
  if (!normalized?.nextStepText) return "none";
  if (normalized.active === false && normalized.status !== "scheduled") return "none";
  if (normalized.status === "dismissed") return "dismissed";
  if (normalized.status === "completed") return "completed";
  if (normalized.status === "scheduled") {
    if (isFollowUpResumed(normalized)) return "scheduled_resumed";
    if (normalized.reminderSentAt) return "scheduled_reminder_sent";
    if (normalized.reminderAt) return "scheduled_reminder_queued";
    return "scheduled_in_app";
  }
  return "suggested";
}

export function getFollowUpState(followUp) {
  const normalized = buildSessionFollowUp(followUp, null);
  const hasFollowUp = Boolean(
    normalized?.nextStepText &&
    (normalized.active !== false || normalized.status === "scheduled")
  );
  return {
    followUp: normalized,
    label: getFollowUpStateLabel(normalized),
    hasFollowUp,
    isConfirmed: isFollowUpConfirmed(normalized),
    reminderQueued: isReminderQueued(normalized),
    reminderSent: Boolean(normalized?.reminderSentAt),
    canQueueEmailReminder: canQueueEmailReminder(normalized),
  };
}

export function mergeSessionFollowUp(primaryFollowUp, fallbackFollowUp, sessionSummary) {
  const primary = buildSessionFollowUp(primaryFollowUp, sessionSummary);
  const fallback = buildSessionFollowUp(fallbackFollowUp, sessionSummary);

  if (!primary) return fallback;
  if (!fallback) return primary;

  const primaryHasActiveSchedule = primary.status === "scheduled" && Boolean(primary.scheduledFor);
  const fallbackHasActiveSchedule = fallback.status === "scheduled" && Boolean(fallback.scheduledFor);
  const scheduleConflict = (
    primaryHasActiveSchedule || fallbackHasActiveSchedule
  ) && primary.scheduledFor !== fallback.scheduledFor;
  if (scheduleConflict) {
    const primaryUpdatedAt = Date.parse(primaryFollowUp?.updatedAt || "") || 0;
    const fallbackUpdatedAt = Date.parse(fallbackFollowUp?.updatedAt || "") || 0;
    return primaryUpdatedAt >= fallbackUpdatedAt ? primary : fallback;
  }

  if (isFinalStatus(primary.status)) {
    return {
      ...fallback,
      ...primary,
      scheduledFor: primary.scheduledFor || fallback.scheduledFor,
      status: primary.status,
      updatedAt: primary.updatedAt || fallback.updatedAt,
    };
  }

  if (isFinalStatus(fallback.status)) {
    return {
      ...primary,
      ...fallback,
      scheduledFor: fallback.scheduledFor || primary.scheduledFor,
      status: fallback.status,
      updatedAt: fallback.updatedAt || primary.updatedAt,
    };
  }

  const primaryIsScheduled = Boolean(primary.scheduledFor);
  const fallbackIsScheduled = Boolean(fallback.scheduledFor);
  if (fallbackIsScheduled && !primaryIsScheduled) {
    return {
      ...primary,
      ...fallback,
      nextStepText: primary.nextStepText || fallback.nextStepText,
      note: primary.note || fallback.note,
      suggestedBy: primary.suggestedBy || fallback.suggestedBy,
      createdByType: primary.createdByType || fallback.createdByType,
      coachConfirmed: Boolean(primary.coachConfirmed || fallback.coachConfirmed),
    };
  }

  if (fallback.scheduledFor && !primary.scheduledFor) {
    return {
      ...primary,
      scheduledFor: fallback.scheduledFor,
      status: "scheduled",
      reminderAt: primary.reminderAt || fallback.reminderAt,
      reminderQueuedAt: primary.reminderQueuedAt || fallback.reminderQueuedAt,
      reminderSentAt: primary.reminderSentAt || fallback.reminderSentAt,
      confirmationEmailQueuedAt: primary.confirmationEmailQueuedAt || fallback.confirmationEmailQueuedAt,
      confirmationEmailSentAt: primary.confirmationEmailSentAt || fallback.confirmationEmailSentAt,
      confirmationEmailLastError: primary.confirmationEmailLastError || fallback.confirmationEmailLastError,
      confirmationEmailLastErrorAt: primary.confirmationEmailLastErrorAt || fallback.confirmationEmailLastErrorAt,
      reminderLastError: primary.reminderLastError || fallback.reminderLastError,
      reminderLastErrorAt: primary.reminderLastErrorAt || fallback.reminderLastErrorAt,
      updatedAt: primary.updatedAt || fallback.updatedAt,
    };
  }

  if (primary.scheduledFor && fallback.scheduledFor) {
    return {
      ...fallback,
      ...primary,
      scheduledFor: primary.scheduledFor || fallback.scheduledFor,
      status: "scheduled",
      reminderAt: primary.reminderAt || fallback.reminderAt,
      reminderQueuedAt: primary.reminderQueuedAt || fallback.reminderQueuedAt,
      reminderSentAt: primary.reminderSentAt || fallback.reminderSentAt,
      confirmationEmailQueuedAt: primary.confirmationEmailQueuedAt || fallback.confirmationEmailQueuedAt,
      confirmationEmailSentAt: primary.confirmationEmailSentAt || fallback.confirmationEmailSentAt,
      confirmationEmailLastError: primary.confirmationEmailLastError || fallback.confirmationEmailLastError,
      confirmationEmailLastErrorAt: primary.confirmationEmailLastErrorAt || fallback.confirmationEmailLastErrorAt,
      reminderLastError: primary.reminderLastError || fallback.reminderLastError,
      reminderLastErrorAt: primary.reminderLastErrorAt || fallback.reminderLastErrorAt,
      updatedAt: primary.updatedAt || fallback.updatedAt,
    };
  }

  return primary;
}

export function formatFollowUpDate(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString("en-ZA", {
    timeZone: FOLLOW_UP_TIMEZONE,
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function toScheduledFollowUpIso(dateValue, timeValue) {
  const dateText = String(dateValue || "").trim();
  const timeText = String(timeValue || "").trim();
  if (!dateText) return null;
  const safeTime = timeText ? `${timeText}:00` : "09:00:00";
  const parsed = new Date(`${dateText}T${safeTime}${FOLLOW_UP_OFFSET}`);
  return Number.isNaN(parsed.getTime()) ? `${dateText}T${safeTime}${FOLLOW_UP_OFFSET}` : parsed.toISOString();
}

function getTimeZoneParts(value) {
  const parsed = new Date(value || "");
  if (Number.isNaN(parsed.getTime())) return null;
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: FOLLOW_UP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return formatter.formatToParts(parsed).reduce((acc, part) => {
    if (part.type !== "literal") acc[part.type] = part.value;
    return acc;
  }, {});
}

export function toFollowUpDateInputValue(value) {
  const parts = getTimeZoneParts(value);
  if (!parts?.year || !parts?.month || !parts?.day) return "";
  return `${parts.year}-${parts.month}-${parts.day}`;
}

export function toFollowUpTimeInputValue(value) {
  const parts = getTimeZoneParts(value);
  if (!parts?.hour || !parts?.minute) return "";
  return `${parts.hour}:${parts.minute}`;
}

function toQueueTimestamp(value) {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function followUpSortKey(row) {
  if (row.followUp.status === "scheduled") {
    return toQueueTimestamp(row.followUp.scheduledFor) || Number.MAX_SAFE_INTEGER;
  }
  return -(toQueueTimestamp(row.followUp.updatedAt) || toQueueTimestamp(row.createdAt) || 0);
}

function isUnresolvedCoachRequirementStatus(value) {
  const status = String(value || "").trim().toLowerCase();
  return status === "open" || status === "claimed" || status === "escalated";
}

export function isCoachActionRequiredFollowUp(record = {}, followUp = null) {
  const normalized = buildSessionFollowUp(followUp ?? record?.followUp, record?.sessionSummary);
  if (!normalized?.nextStepText) return false;
  if (normalized.active === false && normalized.status !== "scheduled") return false;
  const supportConsent = normalizeSupportConsent(record?.supportConsent);
  const unresolvedIntervention = (
    isUnresolvedCoachRequirementStatus(record?.activeInterventionStatus) ||
    isUnresolvedCoachRequirementStatus(record?.interventionStatus) ||
    isUnresolvedCoachRequirementStatus(record?.flagCaseStatus)
  );
  const coachOwned = (
    normalized.createdByType === "coach" ||
    normalized.updatedByType === "coach" ||
    normalized.coachConfirmed
  );
  const supportRequested = supportConsent.directCoachContact === "requested";
  return Boolean(unresolvedIntervention || supportRequested || coachOwned);
}

export function completeOrdinaryFollowUpIfContinued({
  record = {},
  followUp = null,
  actorRole = "member",
  memberContinued = false,
  latestUserText = "",
  completionReason = "member_resumed_session",
  coachActionRequired = null,
} = {}) {
  const normalized = buildSessionFollowUp(followUp ?? record?.followUp, record?.sessionSummary);
  if (!normalized?.nextStepText) return normalized;
  if (String(actorRole || "").toLowerCase() !== "member") return normalized;
  if (normalized.status !== "scheduled") return normalized;
  if (!memberContinued) return normalized;
  const requiresCoachAction = typeof coachActionRequired === "boolean"
    ? coachActionRequired
    : isCoachActionRequiredFollowUp(record, normalized);
  if (requiresCoachAction) return normalized;
  if (!looksLikeCompletionLanguage(latestUserText)) return normalized;
  return {
    ...normalized,
    status: "completed",
    completedAt: new Date().toISOString(),
    completionReason,
    dismissedAt: null,
    updatedAt: new Date().toISOString(),
    updatedByType: "nia",
  };
}

export function buildFollowUpQueueRows(records = [], options = {}) {
  const coachRequiredSessionIds = options?.coachRequiredSessionIds instanceof Set
    ? options.coachRequiredSessionIds
    : null;
  return (Array.isArray(records) ? records : [])
    .map((record) => {
      const followUp = buildSessionFollowUp(record?.followUp, record?.sessionSummary);
      if (!followUp?.nextStepText) return null;
      if (followUp.active === false && followUp.status !== "scheduled") return null;
      const sessionId = record?.id || record?.createdAt || "";
      const coachActionRequired = coachRequiredSessionIds
        ? coachRequiredSessionIds.has(String(sessionId))
          || isCoachActionRequiredFollowUp(record, followUp)
        : isCoachActionRequiredFollowUp(record, followUp);
      return {
        sessionId,
        session: record || null,
        createdAt: record?.createdAt || record?.date || 0,
        client: record?.client || record?.user || record?.userEmail || "Member",
        title:
          record?.title ||
          record?.issueSummary ||
          record?.sessionSummary?.summaryText ||
          "Session",
        coachActionRequired,
        followUpDisplayStatus: getFollowUpDisplayStatus(followUp),
        followUp,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.coachActionRequired !== b.coachActionRequired) return a.coachActionRequired ? -1 : 1;
      const statusOrder = { scheduled: 0, resumed: 1, suggested: 2, completed: 3, dismissed: 4 };
      const leftOrder = statusOrder[a.followUpDisplayStatus] ?? 99;
      const rightOrder = statusOrder[b.followUpDisplayStatus] ?? 99;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      const leftKey = followUpSortKey(a);
      const rightKey = followUpSortKey(b);
      if (a.followUpDisplayStatus === "scheduled" || a.followUpDisplayStatus === "resumed") return leftKey - rightKey;
      return leftKey - rightKey;
    });
}

export function shouldSurfaceMemberFollowUp(followUp, now = Date.now()) {
  const normalized = buildSessionFollowUp(followUp, null);
  if (!normalized?.scheduledFor || normalized.status !== "scheduled") return false;
  const scheduledMs = Date.parse(normalized.scheduledFor);
  if (Number.isNaN(scheduledMs)) return false;
  return scheduledMs - FOLLOW_UP_PROMPT_WINDOW_MS <= now;
}

export function findNextMemberFollowUpSession(records = [], now = Date.now(), bannerState = {}) {
  return (Array.isArray(records) ? records : [])
    .map((record) => {
      const sessionId = record?.id || record?.createdAt || "";
      const bannerKey = `${sessionId}::${String(record?.followUp?.scheduledFor || "")}`;
      const followUp = buildSessionFollowUp({
        ...(record?.followUp || {}),
        ...(bannerState?.[bannerKey] || {}),
      }, record?.sessionSummary);
      if (!shouldSurfaceMemberFollowUp(followUp, now)) return null;
      const scheduledMs = Date.parse(followUp.scheduledFor || "");
      return {
        sessionId,
        title: record?.title || record?.issueSummary || record?.sessionSummary?.summaryText || "Follow-up session",
        sessionSummary: record?.sessionSummary || null,
        followUp,
        scheduledMs: Number.isNaN(scheduledMs) ? Number.MAX_SAFE_INTEGER : scheduledMs,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.scheduledMs - b.scheduledMs)[0] || null;
}
